/**
 * LedgerDrawer — drill-down for any account in the chart of accounts.
 *
 * Wraps GET /api/accounting/ledger/{accountId} (Section 4.5) in a wide
 * modal so users can click an account from AccountingPage, the Trial
 * Balance, or the Balance Sheet and read every posted journal entry that
 * touches it without losing their place.
 *
 * Props:
 *   open          boolean
 *   onClose       () => void
 *   accountId     string | null  — null hides the modal
 *   initialStart  optional ISO date to pre-fill the "from" filter
 *   initialEnd    optional ISO date to pre-fill the "to" filter
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Download, FileText, BookOpen } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { API_URL, getAuthHeaders } from '../../config';
import AppModal from './AppModal';
import LoadingSkeleton from './LoadingSkeleton';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';

const ACCOUNT_TYPE_LABEL = {
  asset:     { ar: 'أصول',       cls: 'bg-ds-primary-50 text-ds-primary-700 dark:bg-ds-primary-900/30 dark:text-ds-primary-300' },
  liability: { ar: 'التزامات',   cls: 'bg-ds-danger-50  text-ds-danger-700  dark:bg-ds-danger-700/20  dark:text-ds-danger-500' },
  equity:    { ar: 'حقوق ملكية', cls: 'bg-purple-50     text-purple-700     dark:bg-purple-900/30     dark:text-purple-300' },
  revenue:   { ar: 'إيرادات',    cls: 'bg-ds-success-50 text-ds-success-700 dark:bg-ds-success-900/30 dark:text-ds-success-500' },
  expense:   { ar: 'مصروفات',    cls: 'bg-orange-50     text-orange-700     dark:bg-orange-900/30     dark:text-orange-300' },
};

const fmtCurrency = (v) =>
  new Intl.NumberFormat('ar-AE', {
    style: 'currency', currency: 'AED', maximumFractionDigits: 2,
  }).format(v ?? 0);

const fmtDate = (raw) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('ar-AE');
  } catch {
    return String(raw).slice(0, 10);
  }
};

export default function LedgerDrawer({
  open,
  onClose,
  accountId,
  initialStart = '',
  initialEnd   = '',
}) {
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate,   setEndDate]   = useState(initialEnd);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  // Reset filters whenever the account being inspected changes — opening
  // a different account should not inherit the previous one's window.
  useEffect(() => {
    if (open) {
      setStartDate(initialStart);
      setEndDate(initialEnd);
      setData(null);
      setError(null);
    }
  }, [open, accountId, initialStart, initialEnd]);

  const fetchLedger = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate)   params.set('end_date',   endDate);
      const qs = params.toString();
      const url = `${API_URL}/accounting/ledger/${accountId}${qs ? `?${qs}` : ''}`;
      const r = await fetch(url, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`فشل تحميل دفتر الأستاذ (${r.status})`);
      setData(await r.json());
    } catch (err) {
      setError(err.message || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [accountId, startDate, endDate]);

  useEffect(() => {
    if (open && accountId) fetchLedger();
  }, [open, accountId, fetchLedger]);

  const exportExcel = () => {
    if (!data || !data.entries?.length) return;
    try {
      const rows = data.entries.map(e => ({
        'التاريخ':       fmtDate(e.date),
        'رقم القيد':     e.entry_number || '',
        'الوصف':         e.description || '',
        'وصف السطر':     e.line_description || '',
        'مدين':          e.debit  || 0,
        'دائن':          e.credit || 0,
        'الرصيد':        e.balance,
      }));
      // Footer rows so the exported sheet stays self-explanatory.
      rows.push({ 'التاريخ': '', 'رقم القيد': '', 'الوصف': 'رصيد الافتتاح',
                  'وصف السطر': '', 'مدين': '', 'دائن': '',
                  'الرصيد': data.opening_balance });
      rows.push({ 'التاريخ': '', 'رقم القيد': '', 'الوصف': 'رصيد الإقفال',
                  'وصف السطر': '', 'مدين': '', 'دائن': '',
                  'الرصيد': data.closing_balance });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      const sheetName = `${data.account.code}-${data.account.name}`.slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const stamp = new Date().toISOString().split('T')[0];
      saveAs(
        new Blob([wbout], { type: 'application/octet-stream' }),
        `ledger-${data.account.code}-${stamp}.xlsx`,
      );
    } catch (err) {
      alert(`فشل التصدير: ${err.message || err}`);
    }
  };

  if (!open || !accountId) return null;

  const account = data?.account;
  const typeStyle = account?.type ? ACCOUNT_TYPE_LABEL[account.type] : null;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      size="full"
      title={
        <div className="flex items-center gap-3 flex-wrap">
          <BookOpen className="w-5 h-5 text-ds-primary-600 dark:text-ds-primary-400" />
          <span className="text-foreground">دفتر الأستاذ</span>
          {account && (
            <>
              <span className="font-mono text-sm text-muted-foreground">
                {account.code}
              </span>
              <span className="text-foreground font-medium">{account.name}</span>
              {typeStyle && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeStyle.cls}`}>
                  {typeStyle.ar}
                </span>
              )}
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Date filter + export */}
        <div className="flex flex-wrap items-end gap-3 pb-3 border-b border-border">
          <Calendar className="w-4 h-4 text-muted-foreground mb-2.5" />
          <div>
            <label className="block text-xs text-muted-foreground mb-1">من تاريخ</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ds-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">إلى تاريخ</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ds-primary-500"
            />
          </div>
          {(startDate || endDate) && (
            <button
              onClick={() => { setStartDate(''); setEndDate(''); }}
              className="text-xs text-ds-primary-600 hover:text-ds-primary-700 dark:text-ds-primary-400 dark:hover:text-ds-primary-300 font-medium px-2 py-2"
            >
              مسح الفترة
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={exportExcel}
            disabled={!data?.entries?.length}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-ds-success-600 hover:bg-ds-success-700 disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors"
          >
            <Download className="w-4 h-4" /> تصدير Excel
          </button>
        </div>

        {/* Summary stats */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SummaryCard label="رصيد الافتتاح" value={data.opening_balance} />
            <SummaryCard label="عدد الحركات" value={data.entries?.length ?? 0} count />
            <SummaryCard label="رصيد الإقفال" value={data.closing_balance} highlight />
          </div>
        )}

        {/* Body */}
        <div>
          {loading && <LoadingSkeleton variant="list" count={6} />}
          {!loading && error && <ErrorState error={error} onRetry={fetchLedger} />}
          {!loading && !error && data && data.entries.length === 0 && (
            <EmptyState
              icon={FileText}
              title="لا توجد حركات"
              description={
                startDate || endDate
                  ? 'لا توجد حركات في الفترة المحددة. جرّب توسيع نطاق التواريخ.'
                  : 'لم يتم تسجيل أي قيد على هذا الحساب بعد.'
              }
            />
          )}
          {!loading && !error && data && data.entries.length > 0 && (
            <LedgerTable entries={data.entries} openingBalance={data.opening_balance} />
          )}
        </div>
      </div>
    </AppModal>
  );
}

const SummaryCard = ({ label, value, count = false, highlight = false }) => (
  <div className={`rounded-xl border p-4 ${
    highlight
      ? 'border-ds-primary-300 dark:border-ds-primary-800 bg-ds-primary-50 dark:bg-ds-primary-900/20'
      : 'border-border bg-card'
  }`}>
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    <p className={`text-xl font-bold font-mono ${
      highlight
        ? 'text-ds-primary-700 dark:text-ds-primary-300'
        : 'text-foreground'
    }`}>
      {count ? value : fmtCurrency(value)}
    </p>
  </div>
);

const LedgerTable = ({ entries, openingBalance }) => (
  <div className="overflow-x-auto rounded-xl border border-border">
    <table className="w-full">
      <thead className="bg-muted">
        <tr>
          <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wide">التاريخ</th>
          <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wide">رقم القيد</th>
          <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wide">الوصف</th>
          <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wide">مدين</th>
          <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wide">دائن</th>
          <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wide">الرصيد</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {/* Opening balance row — only when there's a meaningful prior figure
            so the running balance column is unambiguous from the first
            real entry. */}
        {openingBalance !== 0 && (
          <tr className="bg-muted/40">
            <td colSpan={5} className="px-4 py-2 text-sm text-muted-foreground italic">
              رصيد الافتتاح
            </td>
            <td className="px-4 py-2 font-mono font-bold text-sm text-foreground">
              {fmtCurrency(openingBalance)}
            </td>
          </tr>
        )}
        {entries.map((e, i) => (
          <tr key={i} className="hover:bg-muted">
            <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">{fmtDate(e.date)}</td>
            <td className="px-4 py-3 font-mono text-sm text-muted-foreground">{e.entry_number || '—'}</td>
            <td className="px-4 py-3 text-sm text-foreground max-w-md">
              <div className="truncate">{e.description || '—'}</div>
              {e.line_description && (
                <div className="text-xs text-muted-foreground truncate">{e.line_description}</div>
              )}
            </td>
            <td className="px-4 py-3 font-mono text-sm text-ds-success-600 dark:text-ds-success-500">
              {e.debit > 0 ? fmtCurrency(e.debit) : '—'}
            </td>
            <td className="px-4 py-3 font-mono text-sm text-ds-danger-600 dark:text-ds-danger-500">
              {e.credit > 0 ? fmtCurrency(e.credit) : '—'}
            </td>
            <td className="px-4 py-3 font-mono text-sm font-bold text-foreground">
              {fmtCurrency(e.balance)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
